import { notification } from 'antd';
import lib from '../../assets/utils/lib';

/** Permissions that may queue claims CSV / payment advice exports (API enforces as well). */
export const CLAIMS_EXPORT_PERMISSIONS = [
  'admin.*',
  'claims.reports',
  'claims.read',
  'claims.read_all',
  'claims.read_own',
  'claims.create',
  'claims.update',
  'claims.review',
  'claims.process',
  'claims.approve',
  'claims.reject',
];

export function canCreateClaimsExport(user) {
  const perms = user?.profile?.permissions;
  if (!Array.isArray(perms) || perms.length === 0) return true;
  return perms.some((p) => CLAIMS_EXPORT_PERMISSIONS.includes(p));
}

export function notifyExportQueued() {
  notification.success({
    message: 'Export queued',
    description: 'Your CSV export is queued. You can download it from Exports when ready.',
  });
}

export function notifyExportFailed(error) {
  notification.error({
    message: 'Export failed',
    description:
      error?.response?.data?.message || error?.message || 'Unable to queue export job',
  });
}

/**
 * Claims-page Excel downloads (spec 2026-10-19). Which payment levels have a
 * sheet, and what the button says:
 *   Pending CO  (0): facility's claims only → Export claims breakdown
 *   Pending HM  (1): facility's claims only → Payment advice
 *   Pending CFO (2): months, facilities, facility's claims → Payment advice
 *   Paid / Denied: none.
 * The API enforces the same matrix (GET /v2/admin/claims/payment/sheet).
 */
export function claimsSheetLabel(segment, level) {
  const seg = Number(segment);
  if ((seg === 0 || seg === 1) && level === 'claims') {
    return seg === 0 ? 'Export claims breakdown' : 'Payment advice';
  }
  if (seg === 2 && ['months', 'facilities', 'claims'].includes(level)) {
    return 'Payment advice';
  }
  return null;
}

/** Permissions the sheet route accepts (API enforces as well). */
export const CLAIMS_SHEET_PERMISSIONS = [
  'admin.*',
  'claims.payment',
  'claims.read_all',
  'claims.reports',
  'claims.process',
  'claims.review',
  'claims.approve',
  'claims.reject',
  'reports.claims',
  'reports.financial',
];

export function canDownloadClaimsSheet(user) {
  const perms = user?.profile?.permissions;
  if (!Array.isArray(perms) || perms.length === 0) return true;
  return perms.some((p) => CLAIMS_SHEET_PERMISSIONS.includes(p));
}

export async function downloadClaimsSheet({ segment, level, month, facilityId }) {
  const params = { segment, level };
  if (month) params.month = month;
  if (facilityId) params.facility_id = facilityId;
  try {
    return await lib.downloadClaimsSheetV2(params, `claims-${level}${month ? `-${month}` : ''}.xlsx`);
  } catch (error) {
    notification.error({
      message: 'Download failed',
      description: error?.message || 'Unable to download the sheet',
    });
    return null;
  }
}

export {
  ORG_PAYMENT_ADVICE_EXPORT_TYPE,
  ORG_PAYMENT_ADVICE_PERMISSIONS,
  canExportOrgPaymentAdvice,
  buildOrgPaymentAdviceParams,
  previewOrgPaymentAdvice,
  queueOrgPaymentAdviceExport,
  getOrgJobOutputFormat,
  isServerRenderedOutputFormat,
  isPaApprovedScope,
  DATA_SCOPE_OPTIONS,
  PA_LINE_STATUS_OPTIONS,
  OUTPUT_FORMAT_OPTIONS,
} from '../Organizations/orgPaymentAdviceHelpers';

export {
  FACILITY_ENCOUNTER_SUMMARY_EXPORT_TYPE,
  OUTPUT_FORMAT_OPTIONS as FACILITY_SUMMARY_OUTPUT_FORMATS,
  buildFacilityEncounterSummaryParams,
  previewFacilityEncounterSummary,
  queueFacilityEncounterSummaryExport,
  downloadFacilityEncounterSummarySync,
} from './Analytics/facilityEncounterSummaryHelpers';
